import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

interface SenderState {
  senderId: string | null;
  signature: string | null;
  hasContactFormSubmitted: boolean;
}

interface SenderActions {
  setSenderId: (id: string | null) => void;
  setSignature: (signature: string | null) => void;
  setHasContactFormSubmitted: (hasContactFormSubmitted: boolean) => void;
  getStorageKey: (widgetConfigId: string, companyId: string) => string;
  clearSender: () => void;
}

interface SenderStore extends SenderState {
  actions: SenderActions;
}

export const useSenderStore = create<SenderStore>()(
  persist(
    (set) => ({
      senderId: null,
      signature: null,
      hasContactFormSubmitted: false,

      actions: {
        setSenderId: (id: string | null) => {
          set({ senderId: id });
        },

        setSignature: (signature: string | null) => {
          set({ signature });
        },

        setHasContactFormSubmitted: (hasContactFormSubmitted: boolean) => {
          set({ hasContactFormSubmitted });
        },

        clearSender: () => {
          set({
            senderId: null,
            signature: null,
            hasContactFormSubmitted: false,
          });
        },

        getStorageKey: (widgetConfigId: string, companyId: string) => {
          return `sf-live-chat-sender-${widgetConfigId}-${companyId}`;
        },
      },
    }),
    {
      name: 'sf-live-chat-sender',
      storage: createJSONStorage(() => localStorage),
      // Persist both senderId and signature
      partialize: (state) => ({
        senderId: state.senderId,
        signature: state.signature,
      }),
    },
  ),
);

export const useSenderStoreHydration = () => {
  const [hydrated, setHydrated] = useState(false);

  useEffect(() => {
    const unsubHydrate = useSenderStore.persist.onHydrate(() => {
      setHydrated(false);
    });

    const unsubFinishHydration = useSenderStore.persist.onFinishHydration(
      () => {
        setHydrated(true);
      },
    );

    setHydrated(useSenderStore.persist.hasHydrated());

    return () => {
      unsubHydrate();
      unsubFinishHydration();
    };
  }, []);

  return hydrated;
};
